import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, MessageCircle, User, LogOut, Home, Repeat, Gift, Bell } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { path: '/individual/dashboard',       label: 'Dashboard',    icon: LayoutDashboard },
  { path: '/individual/orders',          label: 'My Orders',    icon: Package },
  { path: '/individual/foldpass',        label: 'FoldPass',     icon: Repeat },
  { path: '/individual/referrals',       label: 'Refer & Earn', icon: Gift },
  { path: '/individual/notifications',   label: 'Notifications',icon: Bell },
  { path: '/individual/chat',            label: 'Support',      icon: MessageCircle },
  { path: '/individual/profile',         label: 'Profile',      icon: User },
];

const IndividualSidebar = ({ unreadMsg = 0, onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unreadNotifications, setUnreadNotifications] = React.useState(0);

  React.useEffect(() => {
    const token = localStorage.getItem('individualToken');
    if (!token) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/individual/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadNotifications(res.data.count || 0);
      } catch { /* non-critical */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = onLogout || (() => {
    localStorage.removeItem('individualToken');
    localStorage.removeItem('individualData');
    navigate('/individual/login');
  });

  return (
    <aside className="hidden md:flex flex-col w-64 flex-shrink-0 bg-white border-r border-slate-200 h-screen sticky top-0">
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
        <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
          <img src="/logo.png" alt="JustFold" className="h-9 w-auto object-contain" />
        </button>
        <button onClick={() => navigate('/')} title="Go to home"
          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
          <Home className="w-4 h-4" />
        </button>
      </div>

      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const active = pathname === path;
          return (
            <button key={path} onClick={() => navigate(path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                active ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}>
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {label === 'Support' && unreadMsg > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadMsg > 9 ? '9+' : unreadMsg}
                </span>
              )}
              {label === 'Notifications' && unreadNotifications > 0 && (
                <span className="w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-slate-100 space-y-3">
        <div className="bg-slate-50 rounded-2xl p-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Need Help?</p>
          <button onClick={() => navigate('/individual/chat')}
            className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            Contact Support
          </button>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 border border-red-100 transition-all">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </aside>
  );
};

export default IndividualSidebar;
